// src/Pages/Hmo/GetStarted.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch } from '../utils/api';
import SubFooter from '../frontend/components/SubFooter';

// 🔧 API CONFIGURATION
const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018').trim().replace(/\/$/, '');

function GetStarted() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    title: '',
    hearAbout: '',
    countryCode: '+234',
    accountType: ''
  });

  // ✅ API dropdown state
  const [titles, setTitles] = useState([]);
  const [hearAboutOptions, setHearAboutOptions] = useState([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    fetchDropdownData();
  }, []);

  // ✅ Fetch dropdowns from API (POST endpoints)
  // ✅ FIXED: Removed manual headers - let authenticatedFetch handle authentication
  const fetchDropdownData = async () => {
    try {
      const [titlesRes, hearAboutRes] = await Promise.allSettled([
        authenticatedFetch(`${API_BASE}/Settings/titles`, 'POST', {}),
        authenticatedFetch(`${API_BASE}/Settings/hear-about`, 'POST', {}),
      ]);

      const titlesData = titlesRes.status === 'fulfilled' && titlesRes.value?.status === 'success' 
        ? titlesRes.value.data 
        : [];
      
      const hearAboutData = hearAboutRes.status === 'fulfilled' && hearAboutRes.value?.status === 'success'
        ? hearAboutRes.value.data
        : [];

      setTitles(titlesData);
      setHearAboutOptions(hearAboutData);
    } catch (error) {
      console.error('Failed to fetch dropdowns:', error);
    } finally {
      setIsLoadingDropdowns(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // ✅ Basic validation (including AccountType)
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      alert('Please enter your full name');
      return;
    }
    if (!formData.phone.trim()) {
      alert('Please enter your phone number');
      return;
    }
    if (!formData.email.trim()) {
      alert('Please enter your email address');
      return;
    }
    if (!formData.title) {
      alert('Please select your role/title');
      return;
    }
    if (!formData.hearAbout) {
      alert('Please tell us how you heard about us');
      return;
    }
    if (!formData.accountType) {
      alert('Please select your account type (Individual or Corporate)');
      return;
    }

    // ✅ Store form data for next step (INCLUDING AccountType)
    localStorage.setItem('signupStep1', JSON.stringify(formData));
    
    // ✅ Navigate to Create Password page
    navigate('/create-password');
  };

  const countryCodes = ['+234', '+1', '+44', '+233', '+254', '+27'];

  // ✅ Render options: API data only (no fallback)
  const renderTitleOptions = () => {
    if (isLoadingDropdowns) return <option value="">Loading...</option>;
    if (titles.length === 0) return <option value="">No options available</option>;
    return (
      <>
        <option value="">Select your role</option>
        {titles.map((item) => (
          <option key={item.id} value={item.id}>{item.name}</option>
        ))}
      </>
    );
  };

  const renderHearAboutOptions = () => {
    if (isLoadingDropdowns) return <option value="">Loading...</option>;
    if (hearAboutOptions.length === 0) return <option value="">No options available</option>;
    return (
      <>
        <option value="">Select how you heard about us</option>
        {hearAboutOptions.map((item) => (
          <option key={item.id} value={item.id}>{item.name}</option>
        ))}
      </>
    );
  };

  return (
    <div className="get-started-page">
      {/* Header */}
      <header className="header-area header-one">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <a href="/" className="brand-logo">
              <img src="images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
            </a>
            <button 
              className="theme-btn style-two" 
              onClick={() => navigate('/')}
              disabled={isLoadingDropdowns}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Form Section */}
      <section className="get-started-form-section py-5" style={{ background: '#fafafa' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              {/* Progress Indicators */}
              <div className="text-center mb-4" data-aos="fade-down">
                <div className="d-inline-flex align-items-center gap-2">
                  <span className="d-inline-block rounded-circle" style={{ width: '12px', height: '12px', background: '#F89423'}}></span>
                  <span className="d-inline-block rounded-circle bg-light border" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-light border" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-light border" style={{ width: '12px', height: '12px' }}></span>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-center mb-4" data-aos="fade-up">Provide contact person's information</h2>

              {/* Info Box */}
              <div className="alert alert-info border-0 mb-4" role="alert" data-aos="fade-up" style={{ backgroundColor: '#f3f0ff', color: '#5a4fcf', border: '1px solid #d4ceff' }}>
                <div className="d-flex align-items-start">
                  <i className="fas fa-info-circle me-2 mt-1" style={{ color: '#5a4fcf' }}></i>
                  <small>Ensure you provide a valid email address as this will serve as the primary email for your business account.</small>
                </div>
              </div>

              {/* Form Card */}
              <div className="card shadow-sm border-0" data-aos="fade-up" data-aos-delay="200">
                <div className="card-body p-4 p-md-5">
                  <form onSubmit={handleSubmit}>
                    {/* First Name & Last Name */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">First name *</label>
                        <input
                          type="text"
                          name="firstName"
                          className="form-control form-control-lg"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          placeholder="Enter first name"
                          style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Last name *</label>
                        <input
                          type="text"
                          name="lastName"
                          className="form-control form-control-lg"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Enter last name"
                          style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                        />
                      </div>
                    </div>

                    {/* Phone & Email */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Phone number *</label>
                        <div className="input-group">
                          <select 
                            name="countryCode"
                            className="form-select"
                            value={formData.countryCode}
                            onChange={handleChange}
                            style={{ 
                              maxWidth: '100px', 
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #e9ecef',
                              borderRight: 'none',
                              borderTopRightRadius: '0',
                              borderBottomRightRadius: '0'
                            }}
                          >
                            {countryCodes.map((code) => (
                              <option key={code} value={code}>{code}</option>
                            ))}
                          </select>
                          <input
                            type="tel"
                            name="phone"
                            className="form-control form-control-lg"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="Enter phone number"
                            style={{ 
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #e9ecef',
                              borderTopLeftRadius: '0',
                              borderBottomLeftRadius: '0'
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control form-control-lg"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="Enter email address"
                          style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                        />
                      </div>
                    </div>

                    {/* Title & How did you hear - API Powered */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Title/Role *</label>
                        <select
                          name="title"
                          className="form-select form-select-lg"
                          value={formData.title}
                          onChange={handleChange}
                          required
                          disabled={isLoadingDropdowns}
                          style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                        >
                          {renderTitleOptions()}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">How did you hear about us? *</label>
                        <select
                          name="hearAbout"
                          className="form-select form-select-lg"
                          value={formData.hearAbout}
                          onChange={handleChange}
                          required
                          disabled={isLoadingDropdowns}
                          style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                        >
                          {renderHearAboutOptions()}
                        </select>
                      </div>
                    </div>

                    {/* Account Type Dropdown */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Account Type *</label>
                      <select
                        name="accountType"
                        className="form-select form-select-lg"
                        value={formData.accountType}
                        onChange={handleChange}
                        required
                        disabled={isLoadingDropdowns}
                        style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}
                      >
                        <option value="">Select an account type</option>
                        <option value="Individual">Individual</option>
                        <option value="Corporate">Corporate</option>
                        <option value="SME">SME</option>
                         <option value="Cluster Group">Cluster Group</option>
                      </select>
                      <small className="text-muted d-block mt-1">
                        <i className="fas fa-info-circle me-1"></i>
                        Select "Corporate" if registering on behalf of a company or organization
                      </small>
                    </div>

                    {/* Continue Button */}
                    <div className="d-grid gap-3 mt-4">
                      <button 
                        type="submit" 
                        className="theme-btn style-one btn-lg py-3"
                        style={{ fontWeight: '600' }}
                        disabled={isLoadingDropdowns}
                      >
                        {isLoadingDropdowns ? 'Loading...' : 'Continue'} <i className="fas fa-arrow-right ms-2"></i>
                      </button>
                    </div>

                    {/* Login Link */}
                    <div className="text-center mt-3">
                      <small className="text-muted">
                        Already a member? <a href="/login" className="text-decoration-none fw-semibold" style={{ color: '#5a4fcf' }}>Login</a>
                      </small>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
     <SubFooter/>
     
    </div>
  );
}

export default GetStarted;